import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Stack } from '@mui/material';

import { useMapData } from '../../../map-data/use-map-data';
import { useComparisonProviderContext } from '../../../providers/ComparisonProvider';
import type { HeatMapCountryMetaData } from '../../../../lcpn-uploader/server/services/parse-map-records/heat-map/heat-map-types';
import { Typography } from '../../Typography';
import { theme } from '../../../styles/theme';

import { DataDisplayTable } from './DataTable';
import { useHeatMapDataTableDefinitions } from './UseHeatMapDataTableDefinitions';
import { ComparisonWarning } from './ComparisonWarning';

export function HeatmapComparisonTable() {
  const { t } = useTranslation();

  const { countryHeatMapMetaData } = useMapData();

  const [countries, setCountries] = useState<HeatMapCountryMetaData[]>([]);
  const [invalidCountryCodes, setInvalidCountryCodes] = useState<string[]>([]);
  const [duplicateCountryCodes, setDuplicateCountryCodes] = useState<string[]>(
    [],
  );
  const { heatMapComparison, setHeatMapComparison } =
    useComparisonProviderContext();

  const { headings, sections } = useHeatMapDataTableDefinitions();

  useEffect(() => {
    if (!!countryHeatMapMetaData && !!heatMapComparison) {
      const validCountries: HeatMapCountryMetaData[] = [];
      const validCodes: string[] = [];
      const invalidCodes: string[] = [];
      const duplicateCodes: string[] = [];
      const seenCodes = new Set<string>();

      for (const code of heatMapComparison.countryCodes) {
        if (seenCodes.has(code)) {
          if (!duplicateCodes.includes(code)) {
            duplicateCodes.push(code);
          }
          continue;
        }

        const country = countryHeatMapMetaData[code];
        if (country) {
          validCountries.push(country);
          validCodes.push(code);
          seenCodes.add(code);
        } else {
          invalidCodes.push(code);
        }
      }

      setCountries(validCountries);

      // Only update warnings if we actually found issues
      if (invalidCodes.length > 0 || duplicateCodes.length > 0) {
        setInvalidCountryCodes(prev =>
          prev.length === 0 ? invalidCodes : prev,
        );
        setDuplicateCountryCodes(prev =>
          prev.length === 0 ? duplicateCodes : prev,
        );
      }

      // If we have invalid or duplicate codes, update context to only include unique valid ones
      if (
        (invalidCodes.length > 0 || duplicateCodes.length > 0) &&
        validCodes.length > 0
      ) {
        setHeatMapComparison({
          ...heatMapComparison,
          countryCodes: validCodes,
        });
      } else if (validCodes.length === 0 && invalidCodes.length > 0) {
        setHeatMapComparison({ ...heatMapComparison, countryCodes: [] });
      }
    }
  }, [countryHeatMapMetaData, heatMapComparison, setHeatMapComparison]);

  return (
    <Stack spacing={4}>
      <div style={{ color: '#008B7F' }}>
        <Typography variant="h2">
          <strong>{t('comparisonTable.heatMap.title')}:</strong>
          <span>
            <br />
            {t('comparisonTable.heatMap.subtitle')}
          </span>
        </Typography>
      </div>
      {(invalidCountryCodes.length > 0 || duplicateCountryCodes.length > 0) && (
        <div
          style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            padding: theme.spacing(4),
            borderRadius: theme.spacing(1),
          }}
        >
          <ComparisonWarning
            message="The following country codes were not found and have been removed from the comparison:"
            items={invalidCountryCodes}
          />
          <ComparisonWarning
            message="The following duplicate country codes have been removed:"
            items={duplicateCountryCodes}
            marginTop={invalidCountryCodes.length > 0 ? theme.spacing(4) : 0}
          />
        </div>
      )}
      {countries.length >= 2 && (
        <DataDisplayTable<HeatMapCountryMetaData>
          headings={headings}
          type={'heatmap'}
          items={countries}
          sections={sections}
        />
      )}
      {countries.length < 2 && (
        <div
          style={{
            textAlign: 'center',
            padding: theme.spacing(8),
            color: '#666',
          }}
        >
          <Typography variant={'p1'}>
            {countries.length === 0
              ? 'No valid countries to compare. Please check the country codes in the URL.'
              : 'At least 2 valid countries are required for comparison.'}
          </Typography>
        </div>
      )}
    </Stack>
  );
}
